import { API_URL } from '@/config/app';
import { baseApi } from '@/store/api/baseApi';
import type { RootState } from '@/store';
import type {
  AssemblyListParams,
  AssemblyMaster,
  ConstituencyListParams,
  ConstituencyMaster,
  CreateAssemblyPayload,
  CreateConstituencyPayload,
  CreateDistrictPayload,
  CreateStatePayload,
  DistrictListParams,
  DistrictMaster,
  LocationImportResult,
  LocationListParams,
  LocationListResponse,
  StateMaster,
  UpdateAssemblyPayload,
  UpdateConstituencyPayload,
  UpdateDistrictPayload,
  UpdateStatePayload,
} from '@/types/location';
import { isApiResponse } from '@/types/api';
import { createAccessToken } from '@/utils/agent';
import { getRtkErrorMessage } from '@/utils/apiResponse';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

function buildLocationQuery(params: LocationListParams = {}): string {
  const searchParams = new URLSearchParams();
  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.status) searchParams.set('status', params.status);
  return searchParams.toString();
}

function buildDistrictQuery(params: DistrictListParams = {}): string {
  const searchParams = new URLSearchParams(buildLocationQuery(params));
  if (params.stateId) searchParams.set('stateId', params.stateId);
  return searchParams.toString();
}

function buildAssemblyQuery(params: AssemblyListParams = {}): string {
  const searchParams = new URLSearchParams(buildDistrictQuery(params));
  if (params.districtId) searchParams.set('districtId', params.districtId);
  if (params.constituencyId) searchParams.set('constituencyId', params.constituencyId);
  return searchParams.toString();
}

function buildConstituencyQuery(params: ConstituencyListParams = {}): string {
  const searchParams = new URLSearchParams(buildDistrictQuery(params));
  if (params.districtId) searchParams.set('districtId', params.districtId);
  return searchParams.toString();
}

async function importLocationFile(
  path: string,
  file: File,
  accessToken: string | null,
  failureMessage: string,
): Promise<{ data: LocationImportResult; meta?: { message: string } } | { error: FetchBaseQueryError }> {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        'x-access-token': createAccessToken(),
      },
      body: formData,
    });

    const json: unknown = await response.json();

    if (!response.ok) {
      return {
        error: {
          status: response.status,
          data: getRtkErrorMessage({ data: json }) ?? failureMessage,
        },
      };
    }

    const payload = isApiResponse<LocationImportResult>(json)
      ? json.data
      : (json as LocationImportResult);
    const message = isApiResponse<LocationImportResult>(json) ? json.message : undefined;

    return {
      data: payload,
      ...(message ? { meta: { message } } : {}),
    };
  } catch {
    return {
      error: {
        status: 'FETCH_ERROR',
        error: failureMessage,
      },
    };
  }
}

export const locationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getStates: builder.query<LocationListResponse<StateMaster>, LocationListParams | void>({
      query: (params) => {
        const query = buildLocationQuery(params ?? {});
        return `/states${query ? `?${query}` : ''}`;
      },
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'States' as const, id })),
              { type: 'States', id: 'LIST' },
            ]
          : [{ type: 'States', id: 'LIST' }],
    }),
    createState: builder.mutation<StateMaster, CreateStatePayload>({
      query: (body) => ({ url: '/states/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'States', id: 'LIST' }],
    }),
    updateState: builder.mutation<StateMaster, { id: string; body: UpdateStatePayload }>({
      query: ({ id, body }) => ({ url: `/states/update/${id}`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'States', id },
        { type: 'States', id: 'LIST' },
        { type: 'Districts', id: 'LIST' },
        { type: 'Assemblies', id: 'LIST' },
        { type: 'Constituencies', id: 'LIST' },
      ],
    }),
    deleteState: builder.mutation<void, string>({
      query: (id) => ({ url: `/states/delete/${id}`, method: 'POST' }),
      invalidatesTags: [
        { type: 'States', id: 'LIST' },
        { type: 'Districts', id: 'LIST' },
        { type: 'Assemblies', id: 'LIST' },
        { type: 'Constituencies', id: 'LIST' },
      ],
    }),

    getDistricts: builder.query<LocationListResponse<DistrictMaster>, DistrictListParams | void>({
      query: (params) => {
        const query = buildDistrictQuery(params ?? {});
        return `/districts${query ? `?${query}` : ''}`;
      },
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Districts' as const, id })),
              { type: 'Districts', id: 'LIST' },
            ]
          : [{ type: 'Districts', id: 'LIST' }],
    }),
    createDistrict: builder.mutation<DistrictMaster, CreateDistrictPayload>({
      query: (body) => ({ url: '/districts/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Districts', id: 'LIST' }],
    }),
    updateDistrict: builder.mutation<DistrictMaster, { id: string; body: UpdateDistrictPayload }>({
      query: ({ id, body }) => ({ url: `/districts/update/${id}`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Districts', id },
        { type: 'Districts', id: 'LIST' },
        { type: 'Assemblies', id: 'LIST' },
      ],
    }),
    deleteDistrict: builder.mutation<void, string>({
      query: (id) => ({ url: `/districts/delete/${id}`, method: 'POST' }),
      invalidatesTags: [
        { type: 'Districts', id: 'LIST' },
        { type: 'Constituencies', id: 'LIST' },
        { type: 'Assemblies', id: 'LIST' },
      ],
    }),

    getAssemblies: builder.query<LocationListResponse<AssemblyMaster>, AssemblyListParams | void>({
      query: (params) => {
        const query = buildAssemblyQuery(params ?? {});
        return `/assemblies${query ? `?${query}` : ''}`;
      },
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Assemblies' as const, id })),
              { type: 'Assemblies', id: 'LIST' },
            ]
          : [{ type: 'Assemblies', id: 'LIST' }],
    }),
    createAssembly: builder.mutation<AssemblyMaster, CreateAssemblyPayload>({
      query: (body) => ({ url: '/assemblies/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Assemblies', id: 'LIST' }],
    }),
    updateAssembly: builder.mutation<AssemblyMaster, { id: string; body: UpdateAssemblyPayload }>({
      query: ({ id, body }) => ({ url: `/assemblies/update/${id}`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Assemblies', id },
        { type: 'Assemblies', id: 'LIST' },
      ],
    }),
    deleteAssembly: builder.mutation<void, string>({
      query: (id) => ({ url: `/assemblies/delete/${id}`, method: 'POST' }),
      invalidatesTags: [{ type: 'Assemblies', id: 'LIST' }],
    }),

    getConstituencies: builder.query<
      LocationListResponse<ConstituencyMaster>,
      ConstituencyListParams | void
    >({
      query: (params) => {
        const query = buildConstituencyQuery(params ?? {});
        return `/constituencies${query ? `?${query}` : ''}`;
      },
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Constituencies' as const, id })),
              { type: 'Constituencies', id: 'LIST' },
            ]
          : [{ type: 'Constituencies', id: 'LIST' }],
    }),
    createConstituency: builder.mutation<ConstituencyMaster, CreateConstituencyPayload>({
      query: (body) => ({ url: '/constituencies/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Constituencies', id: 'LIST' }],
    }),
    updateConstituency: builder.mutation<
      ConstituencyMaster,
      { id: string; body: UpdateConstituencyPayload }
    >({
      query: ({ id, body }) => ({ url: `/constituencies/update/${id}`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Constituencies', id },
        { type: 'Constituencies', id: 'LIST' },
      ],
    }),
    deleteConstituency: builder.mutation<void, string>({
      query: (id) => ({ url: `/constituencies/delete/${id}`, method: 'POST' }),
      invalidatesTags: [{ type: 'Constituencies', id: 'LIST' }],
    }),

    importStates: builder.mutation<LocationImportResult, File>({
      async queryFn(file, api) {
        const { accessToken } = (api.getState() as RootState).auth;
        return importLocationFile('/states/import', file, accessToken, 'State import failed.');
      },
      invalidatesTags: [{ type: 'States', id: 'LIST' }],
    }),
    importDistricts: builder.mutation<LocationImportResult, File>({
      async queryFn(file, api) {
        const { accessToken } = (api.getState() as RootState).auth;
        return importLocationFile(
          '/districts/import',
          file,
          accessToken,
          'District import failed.',
        );
      },
      invalidatesTags: [{ type: 'Districts', id: 'LIST' }],
    }),
    importAssemblies: builder.mutation<LocationImportResult, File>({
      async queryFn(file, api) {
        const { accessToken } = (api.getState() as RootState).auth;
        return importLocationFile(
          '/assemblies/import',
          file,
          accessToken,
          'Assembly import failed.',
        );
      },
      invalidatesTags: [{ type: 'Assemblies', id: 'LIST' }],
    }),
    importConstituencies: builder.mutation<LocationImportResult, File>({
      async queryFn(file, api) {
        const { accessToken } = (api.getState() as RootState).auth;
        return importLocationFile(
          '/constituencies/import',
          file,
          accessToken,
          'Constituency import failed.',
        );
      },
      invalidatesTags: [{ type: 'Constituencies', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetStatesQuery,
  useCreateStateMutation,
  useUpdateStateMutation,
  useDeleteStateMutation,
  useGetDistrictsQuery,
  useCreateDistrictMutation,
  useUpdateDistrictMutation,
  useDeleteDistrictMutation,
  useGetAssembliesQuery,
  useCreateAssemblyMutation,
  useUpdateAssemblyMutation,
  useDeleteAssemblyMutation,
  useGetConstituenciesQuery,
  useCreateConstituencyMutation,
  useUpdateConstituencyMutation,
  useDeleteConstituencyMutation,
  useImportStatesMutation,
  useImportDistrictsMutation,
  useImportAssembliesMutation,
  useImportConstituenciesMutation,
} = locationsApi;
